import clsx from 'clsx';
import React, {
  useCallback, useRef,
} from 'react';
import { useSelector } from 'react-redux';
import { Container, Grid } from '@printdeal/ui-library';
import { useInView } from 'react-intersection-observer';
import {
  FeaturedProductListProps,
  ViewedProductsFeaturedRef,
  FeaturedProductGridItemProps,
  FeaturedProductListPropsDefault,
  ExperimentRecommendedProductsFromCTProps,
} from '../types';
import ProductListCard from '../ProductListCard';
import { inViewObserverCallback } from './helpers';
import Recommendations from '../../../recommendations/Recommendations';
import { handleRecommendationProductImpressions } from '../../../../helpers/dataLayerHelpers/productDataLayerHelper';
import { IProductPage } from '../../../../types/product/productDetailPage';
import {
  handleMultiVariantProductListClick,
} from './FeaturedProductList.events';
import { getFromLocalStorage, LS_KEYS } from '../../../../helpers/window';
// @ts-expect-error
import { selectors as customerSelectors } from '../../../../store/slices/customerSlice';
import { isRecognizedUser } from '../../../../templates/homepage/homepage-helper';
import { useCTRecommendations } from '../../../recommendations/hooks/useCTRecommendations';
import { RecommendationsProps } from '../../../recommendations/types';

const FeaturedProductGridItem = ({
  product,
  handleClick = () => { },
  impressionHandler = () => { },
  cardVariant,
  imagePriority = false,
  refCount,
  maxItems,
}: FeaturedProductGridItemProps) => {
  const observerCallbackOnProducts = useCallback((inView: boolean, entry: IntersectionObserverEntry) => {
    inViewObserverCallback({
      inView,
      entry,
      product,
      refCount,
      impressionHandler,
      maxItems,
    });
  }, [impressionHandler, refCount, product, maxItems]);

  const { ref } = useInView({
    threshold: 1,
    onChange: observerCallbackOnProducts,
    triggerOnce: true,
  });
  return (
    <>
      {
        product.featuredImage && product.path ? (
          <Grid.Item
            ref={ref}
            onClick={() => handleClick(product?.itemIndex, product?.name || '')}
            data-product={JSON.stringify({
              itemName: product.name,
              itemId: product?.id,
              itemCategory: product.productCategory?.categoryName,
              itemIndex: product?.itemIndex,
            })}
            key={product.name}
          >
            <ProductListCard product={product} imagePriority={imagePriority} cardVariant={cardVariant} />

          </Grid.Item>
        ) : null
      }
    </>
  );
};

const RenderFeaturedProductsDefault = (
  {
    refObj,
    maxItems,
    handleClick,
    cardVariant,
    imagePriority,
    productList = [],
    impressionHandler,
    featuredProductsGridClassSet,
  }: FeaturedProductListPropsDefault,
) => (
  <Grid
    className={featuredProductsGridClassSet}
  >
    <>
      {productList.slice(0, maxItems)?.map((item, index) => {
        const ItemWithPosition = { ...item, itemIndex: index + 1 };
        return (
          <FeaturedProductGridItem
            product={ItemWithPosition}
            key={item.id}
            handleClick={handleClick}
            impressionHandler={impressionHandler}
            cardVariant={cardVariant}
            imagePriority={imagePriority}
            refCount={refObj}
            maxItems={maxItems}
          />
        );
      })}
    </>
  </Grid>
);

const RecommendedFeaturedProducts = (
  {
    placementData,
    audience,
    componentName,
    showLoginOrRecentOrders,
  }: ExperimentRecommendedProductsFromCTProps,
) => (
  <Recommendations
    data={placementData.data}
    config={placementData.config}
    consentData={placementData.consentData}
    componentName={componentName}
    isPlacementLoaded={placementData.isPlacementLoaded}
    anonymousCOAMToken={placementData.anonymousCOAMToken}
    itemsPerRow={showLoginOrRecentOrders ? 2 : 3}
    /**
     * Resetting additional padding temporarily
     * till we get the new variant of cards with Vertical variant
     * As we have experiments running and we need visible product images
     */
    className="pd-py-0"
    cardVariant="square"
    containerClassName="pd-px-0"
    clickHandler={(productName: string, productPosition: number) => {
      handleMultiVariantProductListClick(
        productPosition,
        'variant',
        audience,
        productName,
      );
    }}
    onView={
      (products: IProductPage[], analyticsId: string) => {
        handleRecommendationProductImpressions('homepage_recommendedforyou', products, analyticsId);
      }
    }
  />
);

export const FeaturedProductList = ({
  list = [],
  cardVariant,
  maxItems = 6,
  className = '',
  imagePriority = false,
  handleClick = () => { },
  audiencesDataFromSegment,
  impressionHandler = () => { },
  audienceOverRideForPlayground,
  showLoginOrRecentOrders = false,
}: FeaturedProductListProps) => {
  const ref = useRef<ViewedProductsFeaturedRef>({ viewedProducts: [], eventsDispatchedForProducts: [] });
  const accountId: string = useSelector(customerSelectors.getAccountId);
  const isRecognizedUserFlag = isRecognizedUser(accountId);

  /**
   * Get data from CT personalization for Variant
   * Placement Id: PLC-10000957
   */
  const recommendationsProductData = useCTRecommendations('PLC-10000957', 'Homepage', null, 12000) as RecommendationsProps;

  /**
   * if logged or has orders 3 cols if not 2 cols
   */
  const classNamesForFeaturedProductList = clsx(
    'pd-grid-cols-2 pd-items-stretch pd-gap-6',
    {
      'md:pd-grid-cols-2': showLoginOrRecentOrders,
      'md:pd-grid-cols-3': !showLoginOrRecentOrders,
    },
  );
  const emulatedAudienceType = audienceOverRideForPlayground;
  const customerAudienceType = emulatedAudienceType || getFromLocalStorage(LS_KEYS.AUDIENCE_TYPE)
    || audiencesDataFromSegment?.type;

  const recommendedDataWithPlacements = recommendationsProductData.data
  && recommendationsProductData.data.placements.length > 0;

  const recommendedForYouFallback = !recommendationsProductData.consentData?.statistics || !isRecognizedUserFlag
    || recommendationsProductData.isError || !recommendedDataWithPlacements;

  /**
   * Fall back to showing default products if any of the following fails
   * (1) We have the consent statistics as true
   * (2) We have the customer id from store or LS to make a getRecommendations() call
   * (3) We don't have any error while calling getRecommendations CT endpoint (e.g. stale authToken)
   */
  if (recommendedForYouFallback) {
    return (
      <RenderFeaturedProductsDefault
        refObj={ref}
        productList={list}
        maxItems={maxItems}
        handleClick={handleClick}
        cardVariant={cardVariant}
        imagePriority={imagePriority}
        impressionHandler={impressionHandler}
        featuredProductsGridClassSet={classNamesForFeaturedProductList}
      />
    );
  }

  /**
   * Only show Recommendation Component consuming CT API, when the following are all true:
   * - when we are in the in the playground
   * - The logged-in users have to be recognized at all time
   * - CookieConsent statistics will have to be true
   */
  return (
    <Container size="md" className={className}>
      <Grid className="pd-grid-cols-1 pd-items-stretch pd-gap-3">
        <RecommendedFeaturedProducts
          placementData={recommendationsProductData}
          componentName="Homepage"
          audience={customerAudienceType}
          showLoginOrRecentOrders={showLoginOrRecentOrders}
        />
      </Grid>
    </Container>
  );
};
